import type {
  CreatePaymentBody,
  DeletePaymentResponse,
  OwnerOption,
  PossibleDuplicate,
  PossibleDuplicatesResponse,
} from '@pet/shared';
import { ReceiptApiError } from './receiptClient';

// Re-export so wizard code can catch a single error type without reaching into receiptClient.
export { ReceiptApiError };

export interface CreatePaymentResponse {
  paymentId: string;
  transactionIds: string[];
}

async function extractErrorMessage(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: string };
    return body.error ?? `HTTP ${res.status}`;
  } catch {
    return `HTTP ${res.status}`;
  }
}

export async function getOwners(): Promise<OwnerOption[]> {
  const res = await fetch('/api/owners', { credentials: 'include' });
  if (!res.ok) {
    throw new ReceiptApiError(res.status, await extractErrorMessage(res));
  }
  const data = (await res.json()) as { owners: OwnerOption[] };
  return data.owners;
}

// GET /api/possible-duplicates (AD-20). Asked before a save; a match is a warning, never a refusal.
// The total is the draft's line total, the figure the payment will record.
export async function getPossibleDuplicates(params: {
  date: string;
  payee: string;
  totalCents: number;
  ingestionId?: string;
}): Promise<PossibleDuplicate[]> {
  const query = new URLSearchParams({
    date: params.date,
    payee: params.payee,
    totalCents: String(params.totalCents),
  });
  if (params.ingestionId) query.set('ingestionId', params.ingestionId);
  const res = await fetch(`/api/possible-duplicates?${query.toString()}`, { credentials: 'include' });
  if (!res.ok) {
    throw new ReceiptApiError(res.status, await extractErrorMessage(res));
  }
  const data = (await res.json()) as PossibleDuplicatesResponse;
  return data.matches;
}

// POST /api/payments — multipart/form-data with a `payload` JSON string field and an
// optional `file` part. Follows the uploadReceipt pattern: raw fetch, credentials
// included, FormData body, and NO manual Content-Type (the browser sets the boundary).
export async function createPayment(
  body: CreatePaymentBody,
  file?: File | null
): Promise<CreatePaymentResponse> {
  const form = new FormData();
  form.append('payload', JSON.stringify(body));
  if (file) {
    form.append('file', file);
  }
  const res = await fetch('/api/payments', {
    method: 'POST',
    credentials: 'include',
    body: form,
  });
  if (!res.ok) {
    throw new ReceiptApiError(res.status, await extractErrorMessage(res));
  }
  return res.json();
}

// DELETE /api/payments/:id (AD-19). Always sends a JSON body, at least `{}`: the server parses JSON
// on DELETE and refuses an empty JSON body. The reason is optional and sent only when given.
export async function deletePayment(
  paymentId: string,
  reason?: string
): Promise<DeletePaymentResponse['deleted']> {
  const res = await fetch(`/api/payments/${encodeURIComponent(paymentId)}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(reason ? { reason } : {}),
  });
  if (!res.ok) {
    throw new ReceiptApiError(res.status, await extractErrorMessage(res));
  }
  const data = (await res.json()) as DeletePaymentResponse;
  return data.deleted;
}
